import { BModal } from 'bootstrap-vue'
import store_consultations from '@/store/mods/consultations/consultations'
import { clinicNow } from '@/utils/kine-clinical-time'

const deux_chiffres = nombre => (nombre < 10 ? '0' : '') + nombre
const date_iso = date => date.getFullYear() + '-' + deux_chiffres(date.getMonth() + 1) + '-' + deux_chiffres(date.getDate())
const date_valide = texte => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(texte || '')) return null
    const [annee, mois, jour] = texte.split('-').map(Number)
    const date = new Date(annee, mois - 1, jour)
    return date.getFullYear() === annee && date.getMonth() === mois - 1 && date.getDate() === jour ? date : null
}
// Écart en jours calendaires entre deux dates « AAAA-MM-JJ » (indépendant des changements d'heure)
const ecart_jours = (depuis, jusqu_a) => {
    const a = date_valide(depuis), b = date_valide(jusqu_a)
    if (!a || !b) return null
    return Math.round((Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) - Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())) / 86400000)
}
const JOURS_MAX = 365

// RDV-006 : fenêtre « Donner un rendez-vous » ouverte depuis les actions de la consultation en cours (médecin).
// L'heure est facultative : sans heure, seule la date part et l'Accueil fixe l'heure puis prévient le patient.
// La consultation d'origine identifie patient, médecin effectif et prestation côté Clinique ; le bloc patient reste un instantané historique.
export default {
    name: 'rendez-vous',
    components: { BModal },
    props: { target: { type: Object, default: null }, reprogrammer: Boolean, consultation_api: { type: Object, default: null } },
    data() {
        return {
            visible: false, loading: false, erreur: '', contexte: null, kine_fuseau: '', kine_horaire: false,
            date_rdv: '', heure_rdv: '', motif_rdv: '', date_minimale: '', nombre_jours: '',
            jours_max: JOURS_MAX,
            raccourcis: [
                { libelle: '1 semaine', jours: 7 },
                { libelle: '2 semaines', jours: 14 },
                { libelle: '3 semaines', jours: 21 },
                { libelle: '1 mois', mois: 1 }
            ]
        }
    },
    computed: {
        patient() { return this.target ? this.target.patient : this.$store.getters.patient || {} },
        consultationEnCours() { return this.target ? this.target.consultation : this.$store.getters.consultationPatientEnCours || {} },
        userCredentials() { return this.$store.getters.authInfo || {} },
        heure_normalisee() {
            const texte = String(this.heure_rdv || '').trim()
            return /^\d{2}:\d{2}(:\d{2})?$/.test(texte) ? texte.substr(0, 5) : ''
        },
        // « Rendez-vous le jeudi 1 octobre 2026 à 09:30 · dans 6 jours » ou « … · heure fixée par l'Accueil · dans 6 jours »
        resume() {
            const date = date_valide(this.date_rdv)
            const ecart = ecart_jours(this.aujourdhui(), this.date_rdv)
            if (!date || ecart === null || ecart < 0) return ''
            const jour = date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
            const relatif = ecart === 0 ? 'aujourd\'hui' : ecart === 1 ? 'demain' : 'dans ' + ecart + ' jours'
            const heure = this.heure_normalisee ? ' à ' + this.heure_normalisee : this.reprogrammer ? ' · heure à indiquer' : ' · heure fixée par l\'Accueil'
            return 'Rendez-vous le ' + jour + heure + ' · ' + relatif
        }
    },
    watch: {
        // Une date saisie ou choisie met à jour « Dans [N] jours » (vide hors de 1 à 365 jours)
        date_rdv(valeur) {
            const ecart = ecart_jours(this.aujourdhui(), valeur)
            this.nombre_jours = ecart !== null && ecart >= 1 && ecart <= JOURS_MAX ? ecart : ''
        }
    },
    methods: {
        // Jour courant de l'établissement (fuseau de la clinique) ; jour du navigateur si le fuseau est indisponible
        aujourdhui() {
            if (this.kine_horaire) return clinicNow(new Date(), this.kine_fuseau).slice(0, 10)
            const maintenant = new Date()
            if (typeof this.getDatePartsInClinicTimezone === 'function') {
                try {
                    const parts = this.getDatePartsInClinicTimezone(maintenant) || {}
                    const texte = parts.year + '-' + parts.month + '-' + parts.day
                    if (date_valide(texte)) return texte
                } catch (error) { /* fuseau invalide : jour du navigateur */ }
            }
            return date_iso(maintenant)
        },
        // Heure courante de l'établissement « HH:MM » (heure du navigateur si le fuseau est indisponible)
        heure_actuelle() {
            if (this.kine_horaire) return clinicNow(new Date(), this.kine_fuseau).slice(11, 16)
            const maintenant = new Date()
            if (typeof this.getDatePartsInClinicTimezone === 'function') {
                try {
                    const parts = this.getDatePartsInClinicTimezone(maintenant) || {}
                    const texte = parts.hour + ':' + parts.minute
                    if (/^\d{2}:\d{2}$/.test(texte)) return texte
                } catch (error) { /* fuseau invalide : heure du navigateur */ }
            }
            return deux_chiffres(maintenant.getHours()) + ':' + deux_chiffres(maintenant.getMinutes())
        },
        ouvrir(options = {}) {
            if (this.loading) return
            this.kine_horaire = options.kine === true
            this.kine_fuseau = options.fuseau || ''
            if (this.kine_horaire) {
                try { clinicNow(new Date(), this.kine_fuseau) } catch (_) { this.$emit('erreur', 'Le fuseau de l’établissement est absent ou invalide. Vérifiez les Paramètres.'); return }
            }
            const consultation = this.consultationEnCours
            if (!Number.isInteger(Number(consultation.id)) || Number(consultation.id) < 1) return
            const patient = this.patient
            this.contexte = { consultation_id: Number(consultation.id), patient_nom: [patient.nom, patient.prenoms].filter(Boolean).join(' ') || 'Patient', dossier: patient.code || '—' }
            if (this.target) Object.assign(this.contexte, { patient_id: Number(patient.id), rendez_vous_id: this.target.rendez_vous_id })
            this.date_rdv = options.date || ''
            this.heure_rdv = ''
            this.motif_rdv = ''
            this.nombre_jours = ''
            this.erreur = ''
            this.date_minimale = this.aujourdhui()
            this.visible = true
        },
        // Date du raccourci à partir d'aujourd'hui (clinique) ou de « base » ; « Dans 1 mois » garde le jour, borné à la fin du mois visé
        date_dans(raccourci, base) {
            const depart = base || date_valide(this.aujourdhui()) || new Date()
            const date = new Date(depart.getFullYear(), depart.getMonth(), depart.getDate())
            if (raccourci.mois) {
                const jour = date.getDate()
                date.setDate(1)
                date.setMonth(date.getMonth() + raccourci.mois)
                date.setDate(Math.min(jour, new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()))
            } else {
                date.setDate(date.getDate() + (raccourci.jours || 0))
            }
            return date_iso(date)
        },
        raccourci_actif(raccourci) {
            return Boolean(this.date_rdv) && this.date_rdv === this.date_dans(raccourci)
        },
        // Le champ « Dans [N] jours » est mis en avant quand il correspond à la date et qu'aucun raccourci ne la reflète déjà
        nombre_jours_actif() {
            const jours = Number(this.nombre_jours)
            if (this.nombre_jours === '' || !Number.isInteger(jours) || jours < 1 || jours > JOURS_MAX) return false
            return ecart_jours(this.aujourdhui(), this.date_rdv) === jours && !this.raccourcis.some(raccourci => this.raccourci_actif(raccourci))
        },
        choisir_raccourci(raccourci) {
            this.date_rdv = this.date_dans(raccourci)
            this.erreur = ''
        },
        // « Dans [N] jours » : de 1 à 365 jours après aujourd'hui (clinique) ; une saisie incomplète ne touche pas la date
        choisir_nombre_jours() {
            const texte = String(this.nombre_jours === null || this.nombre_jours === undefined ? '' : this.nombre_jours).trim()
            if (!/^\d{1,3}$/.test(texte)) return
            const jours = Number(texte)
            if (jours < 1 || jours > JOURS_MAX) return
            this.date_rdv = this.date_dans({ jours })
            this.erreur = ''
        },
        effacer_heure() { if (!this.loading) this.heure_rdv = '' },
        annuler() { if (!this.loading) this.visible = false },
        avant_fermeture(event) { if (this.loading) event.preventDefault() },
        meme_contexte() {
            return Boolean(this.contexte) && Number(this.consultationEnCours.id) === this.contexte.consultation_id &&
                (!this.target || (Number(this.target.patient.id) === this.contexte.patient_id && this.target.rendez_vous_id === this.contexte.rendez_vous_id))
        },
        valider() {
            if (!this.meme_contexte()) return 'Le contexte patient a changé. Fermez cette fenêtre et rouvrez la consultation.'
            if (!date_valide(this.date_rdv)) return 'Choisissez la date du rendez-vous.'
            if (this.reprogrammer && !this.heure_normalisee) return 'L’heure est obligatoire pour replanifier ce rendez-vous.'
            if (String(this.heure_rdv || '').trim() && !this.heure_normalisee) return 'L\'heure saisie n\'est pas valide. Corrigez-la ou laissez-la vide.'
            const aujourdhui = this.aujourdhui()
            if (this.date_rdv < aujourdhui) return 'La date du rendez-vous doit être aujourd\'hui ou plus tard.'
            if (this.heure_normalisee && this.date_rdv === aujourdhui && this.heure_normalisee <= this.heure_actuelle())
                return 'Cette heure est déjà passée : choisissez une heure plus tard ou laissez l\'heure vide.'
            return ''
        },
        // Instantané historique du patient (chemin serveur hérité) ; les identifiants restent dérivés de la consultation
        bloc_patient() {
            const patient = this.patient
            const sexe = Number(patient.sexe_id)
            return {
                nom_patient: [patient.nom, patient.prenoms].filter(Boolean).join(' '),
                sexe: sexe === 1 ? 'M' : sexe === 2 ? 'F' : '',
                telephone: patient.telephone_mobile || '',
                num_dossier: patient.code || ''
            }
        },
        async enregistrer_rendez_vous() {
            if (this.loading || !this.visible) return
            this.erreur = this.valider()
            if (this.erreur) return
            const heure = this.heure_normalisee
            const data = {
                // Sans heure : la date seule (« AAAA-MM-JJ ») ; l'Accueil fixera l'heure
                date_heure: heure ? this.date_rdv + 'T' + heure : this.date_rdv,
                consultation_id: this.contexte.consultation_id,
                motif: this.motif_rdv.trim() || null,
                medecin: { nom: this.userCredentials.name, titre: this.userCredentials.titre },
                patient: this.bloc_patient()
            }
            this.loading = true
            try {
                let reponse
                if (this.reprogrammer) {
                    const access = await store_consultations.getTableauMedecinAccess()
                    if (this._isDestroyed || !this.meme_contexte()) return
                    if (!access || !access.success || !access.data || !access.data.access_token) throw Error('L’autorisation médecin est indisponible.')
                    reponse = await store_consultations.reprogrammerRendezVousMedecin({ rendez_vous_id: this.contexte.rendez_vous_id,
                        date_heure: data.date_heure, motif: data.motif, access_token: access.data.access_token })
                } else reponse = await (this.consultation_api || store_consultations).donnerRendezVousConsultation(data)
                if (this._isDestroyed) return
                if (!reponse || (this.reprogrammer ? !reponse.rendez_vous_id : reponse.success !== true)) {
                    this.erreur = reponse && typeof reponse.message === 'string' && reponse.message ? reponse.message : 'Le rendez-vous n\'a pas été enregistré. Réessayez.'
                    return
                }
                const message = typeof reponse.message === 'string' && reponse.message ? reponse.message : ''
                this.loading = false
                this.visible = false
                if (Array.isArray(reponse.conflits) && reponse.conflits.length) {
                    this.warningToast(message || 'Rendez-vous enregistré. Attention : ce médecin a déjà un rendez-vous à la même heure.')
                } else {
                    this.successToast(message || (heure ? 'Rendez-vous enregistré.' : 'Rendez-vous enregistré. L\'Accueil fixera l\'heure et préviendra le patient.'))
                }
                this.$emit('enregistre', reponse)
            } catch (error) {
                if (this._isDestroyed) return
                const response_data = error && error.response && error.response.data
                this.erreur = response_data && typeof response_data.message === 'string' && response_data.message ? response_data.message :
                    (error && error.response ? 'Le rendez-vous n\'a pas pu être enregistré. Réessayez.' : 'Le rendez-vous n\'a pas pu être enregistré : la Clinique ne répond pas. Réessayez.')
            } finally {
                if (!this._isDestroyed) this.loading = false
            }
        }
    }
}
